import { AfterViewChecked, AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { EmailSender } from '@models/email/email-campaign';
import { EmailCampaignListService } from '@services/email-campaign-list.service';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastContainerDirective } from 'ngx-toastr';
import { AtlasToastService } from '@services/atlas-toast.service';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import * as _ from 'lodash';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-email-setup-list',
  templateUrl: './email-campaigns-setup.component.html',
  styleUrls: ['./email-campaigns-setup.component.scss'],
  standalone: false
})
export class EmailCampaignSetupComponent implements OnInit, AfterViewInit, AfterViewChecked {
  @ViewChild('modalEmailAddSender') modalEmailAddSender;
  @ViewChild('modalSuccessNewSender') modalSuccessNewSender;
  @ViewChild('modalDeleteSender') modalDeleteSender;
  @ViewChild('modalSuccessActiveSender') modalSuccessActiveSender;
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;
  @ViewChild('page') $page;
  $actionsBar: ElementRef;
  @ViewChild('actionsBar') set content(content: ElementRef) {
    if (content) {
      this.$actionsBar = content;
    }
  }
  faTrash = faTrash;
  setupForm: UntypedFormGroup;
  modalRefEmailAddSender: NgbModalRef;
  modalRefSuccessNewSender: NgbModalRef;
  modalRefDeleteSender: NgbModalRef;
  modalRefSuccessActiveSender: NgbModalRef;

  languages: Array<{ id: string; label: string; value: string }> = [
    { id: 'ita', label: 'language_ita', value: '' },
    { id: 'gbr', label: 'language_eng', value: '' },
    { id: 'fra', label: 'language_fra', value: '' },
    { id: 'deu', label: 'language_deu', value: '' },
    { id: 'esp', label: 'language_esp', value: '' }
  ];

  emailSenderSelcted: EmailSender;
  listActiveSenderEmail: EmailSender[] = [];
  tableEmaiSenderlList: EmailSender[] = [];

  showDropdownLang = true;

  private idEmailAddressToDelete;
  private currentEmailCampaign;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private emailListService: EmailCampaignListService,
    private translateService: TranslateService,
    private loaderService: LoaderService,
    private location: Location,
    private modalService: NgbModal,
    private atlasToast: AtlasToastService,
    private activateRoute: ActivatedRoute,
    private elRef: ElementRef
  ) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initTranslateItemLanguage();
    }, 1000);
  }

  ngOnInit(): void {
    window.addEventListener('scroll', this.scroll, true);

    this.buildSetupForm();
    this.setIdLanguageDefault();
    const idCampaign = this.getIdCampaignFromRouting();
    if (this.isEditingPage(idCampaign)) {
      // editing a exist list
      this.showDropdownLang = false;
      this.loadPageFromIdCampaignForEditing(idCampaign);
    } else {
      // create a new list
      this.showDropdownLang = true;
      this.newPageForCreatingNewList();
    }
  }

  ngAfterViewChecked(): void {
    this.displayScrollIndicator();
  }

  scroll = (): void => {
    this.displayScrollIndicator();
  };

  private isEditingPage(idCampaign) {
    if (!_.isNull(idCampaign)) {
      return idCampaign !== '';
    }
    return false;
  }

  private getIdCampaignFromRouting() {
    return this.activateRoute.snapshot.paramMap.get('id');
  }

  private loadPageFromIdCampaignForEditing(idCampaign) {
    this.loaderService.show();
    const joinCalls: Array<Observable<any>> = new Array<Observable<any>>();
    joinCalls.push(this.emailListService.sigleListCampaignEmail(idCampaign));
    joinCalls.push(this.emailListService.listEmailSender());
    forkJoin(joinCalls)
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(
        response => {
          this.initFormForEdititing(response[0]);
          this.setFormlistEmailSender(response[1]);
        },
        error => {
          this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        }
      );
  }

  private initFormForEdititing(emailCampagn) {
    this.currentEmailCampaign = emailCampagn;
    this.setupForm.patchValue(emailCampagn);
  }

  private newPageForCreatingNewList() {
    this.setIdLanguageDefault();
    this.callApiListEmailSenderForNewList();
  }

  private setFirstItemDropdownActiveSenderEmail(listEmailSender) {
    const firstItemEmailSender = listEmailSender.find(es => es.isActive === true).emailSender;
    this.getItemSetupForm('emailSender').setValue(firstItemEmailSender);
  }

  private callApiListEmailSenderForNewList() {
    this.loaderService.show();
    this.emailListService
      .listEmailSender()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(
        listEmailSender => {
          this.setFormlistEmailSender(listEmailSender);
          this.setFirstItemDropdownActiveSenderEmail(listEmailSender);
        },
        error => {
          this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        }
      );
  }

  private setFormlistEmailSender(listEmailSender) {
    this.tableEmaiSenderlList = listEmailSender;
    this.listActiveSenderEmail = listEmailSender.filter(es => es.isActive === true);
  }

  private buildSetupForm() {
    this.setupForm = this.formBuilder.group({
      idCampaign: ['', []],
      title: ['', [Validators.required]],
      description: [''],
      idLanguage: [''],
      sender: ['', [Validators.required]],
      emailSender: ['', [Validators.required]],
      replyTo: [''],
      companyName: [''],
      companyAddress: [''],
      url: [''],
      companyPhone: [''],
      companyFax: [''],
      status: [''],
      createDate: ['']
    });
  }

  private setIdLanguageDefault() {
    this.getItemSetupForm('idLanguage').patchValue(this.languages[0].id);
  }

  private getItemSetupForm(item) {
    return this.setupForm.get(item);
  }

  private initTranslateItemLanguage() {
    this.languages.forEach(lang => {
      lang.value = this.translateService.instant(lang.label);
    });
    this.languages = this.languages.slice();
  }

  goBack() {
    this.location.back();
  }

  btnActiveMail(id) {
    const idEmailAddress = id;
    this.callApiResendValidator(idEmailAddress);
  }

  private callApiResendValidator(idEmailAddress) {
    this.emailListService.resendValidatorSenderEmail(idEmailAddress).subscribe(
      success => {
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
        this.openModalSuccessActiveSender();
        this.tableEmaiSenderlList.forEach(d => {
          if (d.idEmailAddress === idEmailAddress) {
            d.waitingActivation = true;
          }
        });
        this.tableEmaiSenderlList = this.tableEmaiSenderlList.slice();
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
      }
    );
  }

  openModalSuccessActiveSender() {
    this.modalRefSuccessActiveSender = this.modalService.open(this.modalSuccessActiveSender);
    this.modalRefSuccessActiveSender.result.then(
      result => {},
      reason => {
        this.modalRefSuccessActiveSender.close();
      }
    );
  }

  dismissModalSuccessActiveSender() {
    this.modalRefSuccessActiveSender.close();
  }

  openModalAddEmailSender() {
    this.modalRefEmailAddSender = this.modalService.open(this.modalEmailAddSender);
    this.modalRefEmailAddSender.result.then(
      result => {},
      reason => {
        this.modalRefEmailAddSender.close();
      }
    );
  }

  modalAddEmailSender(data) {
    const emailSender: string = data.sender + '@' + data.domain;
    this.callApiAddNewEmailSender(emailSender);
  }

  private callApiAddNewEmailSender(emailSender: string) {
    this.emailListService.addNewEmailSender(emailSender).subscribe(
      eSender => {
        this.tableEmaiSenderlList.push(eSender);
        this.modalRefEmailAddSender.close();
        this.openModalSuccessNewSender();
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        this.modalRefEmailAddSender.close();
      }
    );
  }

  modalAddEmailSenderDismiss() {
    this.modalRefEmailAddSender.close();
  }

  openModalSuccessNewSender() {
    this.modalRefSuccessNewSender = this.modalService.open(this.modalSuccessNewSender);
    this.modalRefSuccessNewSender.result.then(
      result => {},
      reason => {
        this.modalRefSuccessNewSender.close();
      }
    );
  }

  dismissModalSuccessNewSender() {
    this.modalRefSuccessNewSender.close();
  }

  openModalDeleteEmailSender(id) {
    this.idEmailAddressToDelete = id;
    this.modalRefDeleteSender = this.modalService.open(this.modalDeleteSender);
    this.modalRefDeleteSender.result.then(
      result => {},
      reason => {
        this.dismissModalDeleteEmailSender();
      }
    );
  }

  modaleSeleteEmailSender() {
    const idEmailAddress = this.idEmailAddressToDelete;
    this.callAppiDeleteEmailSender(idEmailAddress);
  }

  dismissModalDeleteEmailSender() {
    this.modalRefDeleteSender.close();
  }

  private callAppiDeleteEmailSender(idEmailAddress) {
    this.emailListService.deleteEmailSender(idEmailAddress).subscribe(
      success => {
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
        this.reloadListAfterDelete();
        this.modalRefDeleteSender.close();
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
        this.dismissModalDeleteEmailSender();
      }
    );
  }

  private reloadListAfterDelete() {
    this.tableEmaiSenderlList = this.tableEmaiSenderlList.filter(e => e.idEmailAddress !== this.idEmailAddressToDelete);
  }

  save() {
    const formData = this.setupForm.value;
    const idCampaign = formData.idCampaign;
    if (this.isEditingPage(idCampaign)) {
      this.callApiUpdateListEmailCampaign(idCampaign, this.currentEmailCampaign, formData);
    } else {
      this.callApiCreateListEmailCampaign(formData);
    }
  }

  private callApiUpdateListEmailCampaign(idCampaign, currentList, formData) {
    this.emailListService.updateListEmailCampaign(idCampaign, currentList, formData).subscribe(
      success => {
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
      },
      () => {
        this.goBack();
      }
    );
  }

  private callApiCreateListEmailCampaign(formData) {
    this.emailListService.createListEmailCampaign(formData).subscribe(
      success => {
        this.atlasToast.success('common_success', null, 6000, this.toastContainer);
      },
      error => {
        this.atlasToast.error('common_error', null, 6000, this.toastContainer);
      },
      () => {
        this.goBack();
      }
    );
  }

  resetForm() {
    const formData = this.setupForm.value;
    const idCampaign = formData.idCampaign;
    if (this.isEditingPage(idCampaign)) {
      this.setupForm.patchValue(this.currentEmailCampaign);
    } else {
      this.setupForm.reset();
    }
  }

  private displayScrollIndicator() {
    if (!(this.$page && this.$actionsBar)) {
      return;
    }
    const $pageAccount: HTMLElement = this.$page.nativeElement;
    const $actionsBar: HTMLElement = this.$actionsBar.nativeElement;
    const $impersonateHeader: HTMLElement = document.getElementById('impersonate-header');

    if (!this.elRef.nativeElement || !this.elRef.nativeElement.closest('body')) {
      return;
    }

    const remainingHeight = window.innerHeight - $pageAccount.getBoundingClientRect().top;

    console.log($pageAccount.getBoundingClientRect().bottom - window.innerHeight);

    if (
      $pageAccount.clientHeight > remainingHeight + 5 &&
      $pageAccount.getBoundingClientRect().bottom - window.innerHeight - $impersonateHeader.getBoundingClientRect().bottom > 50
    ) {
      $actionsBar.classList.add('has--scroll-indicator');
    } else {
      $actionsBar.classList.remove('has--scroll-indicator');
    }
  }
}
